import { Component, Input, OnInit } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import {
  CourseResponsible,
  CurriculumService,
} from '../../../services/curriculum.service';
import { UserService } from '../../../services/user.service';

/**
 * Who answers for one course's material.
 *
 * This is act A of two that must not be confused: here a person is
 * APPOINTED, and one grantor is enough. Confirming a content link between two
 * courses is act B and needs every responsible person on BOTH sides — that
 * lives in the prerequisites card.
 *
 * Appointing by hand is the normal path, not the exception: the Moodle
 * teaching role only reaches a portal account for the minority of lecturers
 * whose email we store (none without a PII release), so the LMS sync leaves
 * most courses without anybody.
 */
@Component({
  selector: 'app-course-responsibles-dialog',
  templateUrl: './course-responsibles-dialog.component.html',
  styleUrl: './course-responsibles-dialog.component.scss',
})
export class CourseResponsiblesDialogComponent implements OnInit {
  @Input() courseId!: string;
  @Input() courseName = '';

  loading = true;
  saving = false;
  mayAppoint = false;
  responsibles: CourseResponsible[] = [];
  users: any[] = [];
  selectedUserId: string | null = null;
  /** Set once a change happened, so the caller can refresh its row. */
  changed = false;

  constructor(
    protected dialogRef: NbDialogRef<CourseResponsiblesDialogComponent>,
    private curriculum: CurriculumService,
    private userService: UserService,
    private toastr: NbToastrService,
  ) {}

  ngOnInit(): void {
    this.load();
    // `listUsers()`, NICHT `listAllUsers()`: letzteres geht auf /admin/users und
    // liefert Nutzer ALLER Mandanten. Im Dialog standen dadurch fremde
    // E-Mail-Adressen zur Auswahl. Geschrieben haette man sie nie bekommen —
    // der Endpunkt prueft `User.client_id == client_id` und antwortet sonst mit
    // 404 —, aber angezeigt wurden sie, und das ist schon zu viel.
    this.userService.listUsers().subscribe({
      next: (users: any) => (this.users = users || []),
      error: () => (this.users = []),
    });
  }

  private load(): void {
    this.loading = true;
    this.curriculum.listResponsibles(this.courseId).subscribe({
      next: (res) => {
        this.responsibles = res?.responsibles || [];
        this.mayAppoint = !!res?.may_appoint;
        this.loading = false;
      },
      error: () => {
        this.responsibles = [];
        this.loading = false;
      },
    });
  }

  /** Users not already responsible here. */
  get pickableUsers(): any[] {
    const taken = new Set(this.responsibles.map((r) => r.user_id));
    return (this.users || []).filter((u) => !taken.has(u.id));
  }

  userLabel(u: any): string {
    // Das Portal fuehrt `name`, nicht first/last — mit den falschen Feldern
    // stand in der Auswahl nur die E-Mail.
    const name = (u?.name || '').trim();
    if (name && u?.email) return `${name} · ${u.email}`;
    return name || u?.email || u?.id;
  }

  sourceLabel(r: CourseResponsible): string {
    return r.source === 'lms_role' ? 'aus Moodle-Lehrrolle' : 'benannt';
  }

  add(): void {
    if (!this.selectedUserId || this.saving) return;
    this.saving = true;
    this.curriculum.grantResponsible(this.courseId, this.selectedUserId).subscribe({
      next: () => {
        this.selectedUserId = null;
        this.saving = false;
        this.changed = true;
        this.load();
      },
      error: () => {
        this.saving = false;
        this.toastr.danger('Benennen fehlgeschlagen', 'Verantwortliche');
      },
    });
  }

  remove(r: CourseResponsible): void {
    if (this.saving) return;
    this.saving = true;
    this.curriculum.revokeResponsible(this.courseId, r.user_id).subscribe({
      next: () => {
        this.saving = false;
        this.changed = true;
        this.load();
      },
      error: () => {
        this.saving = false;
        this.toastr.danger('Entziehen fehlgeschlagen', 'Verantwortliche');
      },
    });
  }

  /** Materialise responsibility from the Moodle teaching roles, client-wide. */
  syncFromLms(): void {
    if (this.saving) return;
    this.saving = true;
    this.curriculum.syncLmsResponsibles().subscribe({
      next: (stats) => {
        this.saving = false;
        this.changed = true;
        const unreachable = (stats?.candidates ?? 0) - (stats?.matched ?? 0);
        // The gap is the interesting number: those lecturers exist in Moodle
        // but have no portal account we can attach a responsibility to.
        this.toastr.success(
          `${stats?.created ?? 0} übernommen` +
            (unreachable > 0
              ? ` · ${unreachable} Lehrende ohne Portal-Konto, die musst du von Hand benennen`
              : ''),
          'Aus Moodle-Lehrrollen',
        );
        this.load();
      },
      error: () => {
        this.saving = false;
        this.toastr.danger('Übernahme fehlgeschlagen', 'Aus Moodle-Lehrrollen');
      },
    });
  }
}
